/**
 * /portal — ver `MEMBERSHIP_ROUTE.PORTAL` (alias legacy: /pending-approval, /mi-acceso).
 *
 * Página única post-pago: espera la aprobación del equipo (polling a Clerk + sync
 * membresía) y, cuando `approved`, muestra en esta misma página el link privado de
 * ingreso (`telegram_invite_url`, distinto por membresía, asignado en admin). Si
 * está aprobado pero el link aún no está cargado, sigue polleando hasta mostrarlo.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import {
  MembershipPortalCard,
  MembershipPortalIconBadge,
  MembershipPortalShell,
} from "@/components/membership/MembershipPortalShell";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useMembershipFunnelEnglish } from "@/hooks/useMembershipFunnelEnglish";
import { getUserDisplayName } from "@/lib/auth";
import { readMembershipProfileFromMeta } from "../../shared/membershipClerkMetadata";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { useClerk, useSession, useUser } from "@clerk/clerk-react";
import {
  ArrowUpRight,
  Bell,
  CheckCircle2,
  Clock,
  Copy,
  Crown,
  ExternalLink,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { startVipMembershipVideoCheckout } from "@/lib/vipMembershipVideoCheckout";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { detectCountryCode } from "@/lib/countryDetection";

const POLL_INTERVAL_MS = 12_000;
const MEMBERSHIP_SYNC_MIN_MS = 60_000; // throttle al sync /api/sync-my-membership
const NOTIFY_COOLDOWN_MS = 30 * 60 * 1000;
const STORAGE_KEY_PREFIX = "pending_approval_notify_";

// ── i18n inline ──────────────────────────────────────────────────────────────
// /portal es la última pantalla del flow VIP/Exclusive — el visitor entra ya
// Portal SIEMPRE en inglés (post-compra = foco US/intl).
type PortalCopy = {
  seoTitle: string;
  seoDescription: string;
  greetingPrefix: string;
  descAfterPayInvitation: string;
  descAfterPay: string;
  descPending: string;
  descFree: string;
  /** VIP pago recibido vía el flow Clerk-gated (?from_paid=vip). */
  descPaidVip: string;
  /** Badge "Pago confirmado · Asignando link" para el flow VIP gated. */
  statusPaidPending: string;
  telegramAliasLabel: string;
  myMembership: string;
  myStatus: string;
  statusWaiting: string;
  statusApproved: string;
  approvedTitle: string;
  approvedDesc: string;
  privateLinkLabel: string;
  copyLink: string;
  enterNow: string;
  linkPersonalWarning: string;
  linkAppearingSoon: string;
  loggedInAs: string;
  contact: string;
  signOut: string;
  cooldownTitle: string;
  cooldownDesc: (m: number) => string;
  notifiedTitle: string;
  notifiedDesc: string;
  errorNotify: string;
  /** Upsell free → VIP mientras espera la aprobación (o ya aprobado free). */
  upgradeTitle: string;
  upgradeBody: string;
  upgradeButton: string;
  upgradeProcessing: string;
};

const PORTAL_COPY_EN: PortalCopy = {
  seoTitle: "My account is under review",
  seoDescription: `Waiting for approval to join ${BRAND_PERSON_NAME}'s exclusive community.`,
  greetingPrefix: "Hi",
  descAfterPayInvitation: `Payment received. Waiting for the ${BRAND_PERSON_NAME} team to approve me. As soon as they do, my private access link appears right here.`,
  descAfterPay: `Payment received. The ${BRAND_PERSON_NAME} team is reviewing my account and will send my private access link soon.`,
  descPending: `My account is waiting for approval. The ${BRAND_PERSON_NAME} team will approve me as soon as possible.`,
  descFree: `Registration complete! The team will review my details and send my personal access link by email once approved. It usually takes a few hours.`,
  descPaidVip: `Payment received! My VIP access to ${BRAND_PERSON_NAME} is being processed. The team is reviewing it — as soon as I'm approved, my private link appears here and I'll get an email. Usually within a few hours.`,
  statusPaidPending: "Payment confirmed · Assigning link",
  telegramAliasLabel: "My registered Telegram",
  myMembership: "Membership",
  myStatus: "Status",
  statusWaiting: "Waiting for approval",
  statusApproved: "Approved",
  approvedTitle: "I'm in!",
  approvedDesc: `My membership with ${BRAND_PERSON_NAME} is active. Here's my private access link.`,
  privateLinkLabel: "My private access link",
  copyLink: "Copy",
  enterNow: "Enter now",
  linkPersonalWarning: "This link is personal. Don't share it — the team can revoke access if misuse is detected.",
  linkAppearingSoon: "Approved! My private link will appear here in a few seconds.",
  loggedInAs: "Signed in as",
  contact: "Contact",
  signOut: "Sign out",
  cooldownTitle: "Hold on",
  cooldownDesc: (m) => `You already notified the team. Try again in ${m} minute${m !== 1 ? "s" : ""}.`,
  notifiedTitle: "Notification sent",
  notifiedDesc: "The team received the notification. You'll be approved as soon as possible.",
  errorNotify: "Couldn't send the notification.",
  upgradeTitle: "Want full VIP access?",
  upgradeBody: `While I wait, I can upgrade to VIP (1 month) and unlock all of ${BRAND_PERSON_NAME}'s exclusive content. My account is already set up — I just complete the payment.`,
  upgradeButton: "Upgrade to VIP now",
  upgradeProcessing: "Opening checkout…",
};

// Post-purchase surfaces render in ENGLISH always (decisión Andrés
// 2026-07-02): la membresía paga es foco US/intl; el idioma del sitio no
// aplica acá. Los emails de acceso siguen bilingües por país (approve.ts).
const t = PORTAL_COPY_EN;

export default function PendingApproval() {
  useMembershipFunnelEnglish();
  // Site language is tracking-only on this page (UI is always EN, see t).
  const { currentLanguage } = useLanguage();
  useSEO({
    title: t.seoTitle,
    description: t.seoDescription,
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.PORTAL}`,
    robots: "noindex, nofollow",
  });
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const { signOut } = useClerk();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const displayName = getUserDisplayName(user);
  const profileTelegram = readMembershipProfileFromMeta(
    user?.unsafeMetadata as Record<string, unknown>,
  ).telegram;
  const fromVip = searchParams.get("from_vip") === "1" || searchParams.get("from_special_offer") === "1";
  const planTierHint = (searchParams.get("plan_tier") || "").trim().toLowerCase();
  const fromBasicPurchase = searchParams.get("from_basic") === "1" || planTierHint === "basic";
  const fromStandardPurchase =
    searchParams.get("from_standard") === "1" || planTierHint === "standard";
  /** Solicitud de acceso SIN pago (dual template VIP): no afirmar "pago recibido". */
  const isAccessRequest = searchParams.get("access_request") === "1";
  /** Viene de un link de invitación generado desde el admin. */
  const fromInvitation = searchParams.get("from_invitation") === "1";
  /** Viene del flujo Free Join (no hubo pago — acceso gratuito pendiente de aprobación). */
  const fromFree = searchParams.get("from_free") === "true" || searchParams.get("from_free") === "1";
  /**
   * Viene del flujo Clerk-gated VIP (DualCTAVideoLanding → Clerk → checkout
   * tras checkout VIP). El webhook ya creó el
   * row user_memberships con status='pending_approval'; este copy solo
   * comunica al usuario que el pago se procesó y está esperando aprobación.
   */
  const fromPaidParam = (searchParams.get("from_paid") || "").trim().toLowerCase();
  const fromPaidVip = fromPaidParam === "vip";
  const stripeSessionId = (searchParams.get("session_id") || "").trim();
  const hasStripePaymentSignal = stripeSessionId.startsWith("cs_");
  const postCheckoutMessaging =
    !isAccessRequest &&
    !fromFree &&
    !fromPaidVip &&
    (hasStripePaymentSignal ||
      fromVip ||
      fromBasicPurchase ||
      fromStandardPurchase ||
      planTierHint === "vip" ||
      planTierHint === "platinum" ||
      planTierHint === "devil");

  const [approved, setApproved] = useState(false);
  const [accessUrl, setAccessUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [polling, setPolling] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [manualChecking, setManualChecking] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [hasNotified, setHasNotified] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [upgradeFiring, setUpgradeFiring] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const approvalHandledRef = useRef(false);
  const lastSyncAtRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // ── PostHog tracking ───────────────────────────────────────────────────────
  // posthog cargado en main.tsx (window.posthog). Helper safe ante ad-blockers
  // y antes de init. Eventos del último tramo del funnel post-pago:
  // portal_pending_view → portal_approved_view → portal_telegram_link_clicked.
  const track = useCallback((event: string, props: Record<string, unknown> = {}) => {
    if (typeof window === "undefined") return;
    const ph = (window as Window & {
      posthog?: { capture: (e: string, p?: object) => void };
    }).posthog;
    ph?.capture(event, {
      ...props,
      language: currentLanguage,
      from_vip: fromVip,
      from_invitation: fromInvitation,
      plan_tier_hint: planTierHint || undefined,
      from_paid: fromPaidParam || undefined,
      from_paid_vip: fromPaidVip,
    });
  }, [currentLanguage, fromVip, fromInvitation, planTierHint, fromPaidParam, fromPaidVip]);

  // View al mount (visitor llega del registro o vuelve a chequear estado).
  useEffect(() => {
    if (!email) return;
    track("portal_pending_view", { email_present: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const clearAll = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  // Cooldown del botón "Avisar al equipo"
  useEffect(() => {
    if (!email) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${email}`;
    const tick = () => {
      const last = localStorage.getItem(storageKey);
      if (!last) {
        setCooldownRemaining(0);
        setHasNotified(false);
        return;
      }
      const ts = parseInt(last, 10);
      if (isNaN(ts)) {
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setHasNotified(false);
        return;
      }
      const elapsed = Date.now() - ts;
      const remaining = NOTIFY_COOLDOWN_MS - elapsed;
      if (remaining <= 0) {
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setHasNotified(false);
      } else {
        setCooldownRemaining(remaining);
        setHasNotified(true);
      }
    };
    tick();
    cooldownRef.current = setInterval(tick, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [email]);

  const handleApproved = useCallback(
    (url: string | null) => {
      if (!mountedRef.current) return;
      const wasApproved = approvalHandledRef.current;
      setApproved(true);
      if (url) setAccessUrl(url);
      // Solo dejamos de pollear cuando el link final ya está asignado. Si la cuenta
      // está aprobada pero el equipo todavía no cargó el link, seguimos polleando
      // para mostrarlo en esta misma página apenas aparezca.
      if (url && !approvalHandledRef.current) {
        approvalHandledRef.current = true;
        clearAll();
        track("portal_approved_view", { has_access_url: true });
      } else if (!wasApproved) {
        track("portal_approved_view", { has_access_url: false });
      }
    },
    [clearAll, track],
  );

  const copyAccessUrl = useCallback(async () => {
    if (!accessUrl) return;
    try {
      await navigator.clipboard.writeText(accessUrl);
      setCopied(true);
      track("portal_telegram_link_copied");
      toast({ title: "Link copied" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  }, [accessUrl, toast, track]);

  const checkApproval = useCallback(
    async (isManual = false) => {
      if (!user || !mountedRef.current) return;
      if (isManual && mountedRef.current) setManualChecking(true);
      if (mountedRef.current) setPolling(true);
      try {
        // Sync DB → Clerk (throttled, salvo manual)
        if (session) {
          const now = Date.now();
          const shouldSync =
            isManual ||
            lastSyncAtRef.current === 0 ||
            now - lastSyncAtRef.current >= MEMBERSHIP_SYNC_MIN_MS;
          if (shouldSync) {
            const token = await session.getToken();
            if (token) {
              lastSyncAtRef.current = now;
              try {
                await fetch("/api/sync-my-membership", {
                  headers: { Authorization: `Bearer ${token}` },
                });
              } catch {
                /* network hiccup — ignore */
              }
            }
          }
        }
        const refreshed = await user.reload();
        if (!mountedRef.current) return;
        setLastChecked(new Date());
        if (refreshed.publicMetadata?.approved === true) {
          const url =
            (refreshed.publicMetadata?.telegram_invite_url as string | undefined)?.trim() || null;
          handleApproved(url);
          if (url) return;
        }
      } catch {
        /* ignore */
      }
      if (mountedRef.current) {
        setPolling(false);
        if (isManual) setManualChecking(false);
      }
    },
    [user, session, handleApproved],
  );

  // Auto-poll
  useEffect(() => {
    if (!user?.id || !sessionLoaded) return;
    if (user.publicMetadata?.approved === true) {
      const url =
        (user.publicMetadata?.telegram_invite_url as string | undefined)?.trim() || null;
      handleApproved(url);
      if (url) return;
    }
    void checkApproval();
    intervalRef.current = setInterval(() => {
      void checkApproval();
    }, POLL_INTERVAL_MS);
    return () => clearAll();
  }, [
    user?.id,
    sessionLoaded,
    user?.publicMetadata?.approved,
    user?.publicMetadata?.telegram_invite_url,
    checkApproval,
    handleApproved,
    clearAll,
  ]);

  const notifyAdmin = useCallback(async () => {
    if (!user || !email) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${email}`;
    const last = localStorage.getItem(storageKey);
    if (last) {
      const ts = parseInt(last, 10);
      const remaining = NOTIFY_COOLDOWN_MS - (Date.now() - ts);
      if (remaining > 0) {
        const min = Math.ceil(remaining / 60000);
        toast({
          title: t.cooldownTitle,
          description: t.cooldownDesc(min),
          variant: "destructive",
        });
        return;
      }
    }
    setNotifying(true);
    try {
      const res = await fetch("/api/admin/notify-pending-subscriber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: displayName,
          clerkUserId: user.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === "banned" || data.banned) {
          toast({
            title: "Account blocked",
            description: "Too many notifications. Your account was blocked for abuse.",
            variant: "destructive",
            duration: 8000,
          });
          setTimeout(() => signOut(), 3000);
          return;
        }
        if (data.error === "cooldown_active") {
          const min = Math.ceil((data.remaining_ms ?? NOTIFY_COOLDOWN_MS) / 60000);
          toast({
            title: t.cooldownTitle,
            description: t.cooldownDesc(min),
            variant: "destructive",
          });
          return;
        }
        throw new Error(data.error || t.errorNotify);
      }
      localStorage.setItem(storageKey, Date.now().toString());
      setHasNotified(true);
      setCooldownRemaining(NOTIFY_COOLDOWN_MS);
      toast({
        title: t.notifiedTitle,
        description: t.notifiedDesc,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : t.errorNotify,
        variant: "destructive",
      });
    } finally {
      setNotifying(false);
    }
  }, [user, email, displayName, toast, signOut]);

  // Loading — skeleton + dock placeholder (less layout shift on mobile)
  if (!userLoaded || !sessionLoaded) {
    return (
      <MembershipPortalShell
        topSlot={
          <div className="flex justify-center">
            <div className="portal-status-pill border-white/10 bg-white/5 text-zinc-500">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Loading…
            </div>
          </div>
        }
        bottomDock={
          <div className="space-y-2">
            <div className="h-12 w-full animate-pulse rounded-xl bg-white/10" />
            <div className="h-11 w-full animate-pulse rounded-xl bg-white/5" />
          </div>
        }
      >
        <MembershipPortalCard className="animate-pulse p-5 sm:p-8">
          <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-white/10" />
          <div className="mx-auto mb-3 h-6 w-48 rounded-lg bg-white/10" />
          <div className="mx-auto mb-6 h-4 w-64 max-w-full rounded bg-white/5" />
          <div className="mb-3 h-16 rounded-2xl bg-white/[0.06]" />
          <div className="mb-3 h-20 rounded-2xl bg-white/[0.04]" />
          <div className="flex items-center justify-center gap-2 py-3 text-xs text-zinc-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Loading your account…
          </div>
        </MembershipPortalCard>
      </MembershipPortalShell>
    );
  }

  if (!user) return <Navigate to={MEMBERSHIP_ROUTE.REGISTRO} replace />;

  // ── Datos de la cuenta (vista estilo CRM, visible en espera y al aprobar) ──
  const membershipLevel = (
    (user.publicMetadata?.membership_level as string | undefined) ||
    planTierHint ||
    ""
  ).toLowerCase();
  const vipDisplay = "Exclusive";
  const membershipFallback = "Membership";
  const planLabel =
    membershipLevel === "vip"
      ? vipDisplay
      : membershipLevel === "platinum"
        ? "Platinum"
        : membershipLevel === "standard"
          ? "Standard"
          : membershipLevel === "basic"
            ? "Basic"
            : membershipLevel
              ? membershipLevel.charAt(0).toUpperCase() + membershipLevel.slice(1)
              : membershipFallback;
  const initial = (displayName || email || "?").trim().charAt(0).toUpperCase();

  // ── Upsell free → VIP ──────────────────────────────────────────────────────
  // El miembro free entró a Clerk y quedó pending (no asignado). Desde acá puede
  // convertir a VIP: dispara el mismo checkout que el CTA VIP del landing, con su
  // email Clerk ya cargado; post-pago → /portal/complete-profile (API) si falta perfil.
  // resto→Stripe (lo decide startVipMembershipVideoCheckout por país).
  // free explícito en Clerk, o recién llegado por from_free antes de que el sync
  // de publicMetadata (free-join → syncMembershipToClerk) refleje el nivel.
  // Nunca para niveles pagos (vip/standard/...) ni para el flow VIP gated.
  const isFreeMember =
    !fromPaidVip && (membershipLevel === "free" || (fromFree && membershipLevel === ""));
  const handleUpgradeToVip = () => {
    if (upgradeFiring) return;
    setUpgradeFiring(true);
    track("portal_upgrade_vip_clicked", { from_membership_level: membershipLevel || "free" });
    const buyerEmail = user.primaryEmailAddress?.emailAddress ?? "";
    const countryCode = getServerTemplateConfig()?.country ?? detectCountryCode() ?? "US";
    void startVipMembershipVideoCheckout({
      countryCode,
      pathname: window.location.pathname,
      languageForPlans: countryCode === "AR" ? "es-AR" : "en-US",
      email: buyerEmail,
    });
  };
  const upgradeVipCard = isFreeMember ? (
    <div className="space-y-3 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 to-purple-500/10 p-4">
      <div className="flex items-center gap-2">
        <Crown className="h-4 w-4 shrink-0 text-amber-300" />
        <span className="text-sm font-semibold text-amber-100">{t.upgradeTitle}</span>
      </div>
      <p className="text-xs leading-relaxed text-amber-100/80">{t.upgradeBody}</p>
      <Button
        className="ios-press h-11 w-full gap-2 bg-gradient-to-r from-amber-400 to-amber-500 font-semibold text-black hover:from-amber-300 hover:to-amber-400"
        onClick={handleUpgradeToVip}
        disabled={upgradeFiring}
      >
        {upgradeFiring ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            {t.upgradeProcessing}
          </>
        ) : (
          <>
            <Crown className="w-4 h-4" />
            {t.upgradeButton}
            <ArrowUpRight className="w-4 h-4 opacity-80" />
          </>
        )}
      </Button>
    </div>
  ) : null;

  /** Visual progress: Payment → Review → Access (PWA stepper). */
  const progressSteps = (
    <ol className="portal-stepper px-1 py-1" aria-label="Membership progress">
      {[
        {
          key: "paid",
          label: fromFree || isAccessRequest ? "Registered" : "Paid",
          done: true,
          active: false,
        },
        {
          key: "review",
          label: "Review",
          done: approved,
          active: !approved,
        },
        {
          key: "access",
          label: "Access",
          done: approved && Boolean(accessUrl),
          active: approved && !accessUrl,
        },
      ].map((step, i) => (
        <li
          key={step.key}
          className={
            step.done
              ? "portal-step is-done text-emerald-200/90"
              : step.active
                ? "portal-step is-active text-amber-100"
                : "portal-step is-todo text-zinc-500"
          }
        >
          <span className="portal-step-dot" aria-hidden>
            {step.done ? "✓" : i + 1}
          </span>
          <span>{step.label}</span>
        </li>
      ))}
    </ol>
  );

  const liveStatusPill = (
    <div className="flex justify-center">
      <div
        className={
          approved
            ? "portal-status-pill border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
            : "portal-status-pill border-amber-500/35 bg-amber-500/10 text-amber-100"
        }
        role="status"
        aria-live="polite"
      >
        {polling || manualChecking ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : approved ? (
          <CheckCircle2 className="h-3 w-3" aria-hidden />
        ) : (
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-50" />
            <span className="relative inline-flex h-full w-full rounded-full bg-amber-400" />
          </span>
        )}
        {approved
          ? accessUrl
            ? "Access ready"
            : "Approved · link loading…"
          : polling || manualChecking
            ? "Checking status…"
            : lastChecked
              ? `Live · checked ${lastChecked.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`
              : "Waiting for approval"}
      </div>
    </div>
  );

  const memberDataCard = (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-zinc-100 ring-1 ring-white/10"
          aria-hidden
        >
          {initial}
        </div>
        <div className="min-w-0">
          <p className="truncate font-ios-display font-semibold tracking-tight text-zinc-50">
            {displayName || "Member"}
          </p>
          <p className="truncate text-xs text-zinc-400">{email || "—"}</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-xl border border-white/5 bg-black/30 px-2.5 py-2">
          <span className="text-zinc-500">{t.myMembership}</span>
          <div className="font-medium text-zinc-100">{planLabel}</div>
        </div>
        <div className="rounded-xl border border-white/5 bg-black/30 px-2.5 py-2">
          <span className="text-zinc-500">{t.myStatus}</span>
          <div
            className={approved ? "font-medium text-emerald-300" : "font-medium text-amber-300"}
            aria-live="polite"
          >
            {approved
              ? t.statusApproved
              : fromPaidVip || postCheckoutMessaging
                ? t.statusPaidPending
                : t.statusWaiting}
          </div>
        </div>
      </div>
      {(fromPaidVip || postCheckoutMessaging) && !approved ? (
        <p className="mt-2 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-[11px] leading-snug text-emerald-200/90 ring-1 ring-emerald-500/20">
          Payment confirmed. Your private link appears here after the team approves.
        </p>
      ) : null}
    </div>
  );

  // Approved → private link on this page (sticky Enter CTA = thumb zone)
  if (approved) {
    return (
      <MembershipPortalShell
        topSlot={liveStatusPill}
        bottomDock={
          accessUrl ? (
            <>
              <Button
                asChild
                className="ios-press h-12 w-full gap-2 bg-[#0088cc] text-base font-semibold text-white hover:bg-[#0099d8] active:scale-[0.98]"
              >
                <a
                  href={accessUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("portal_telegram_link_clicked", {
                      url_host: (() => {
                        try {
                          return new URL(accessUrl).host;
                        } catch {
                          return "";
                        }
                      })(),
                    })
                  }
                >
                  <MessageCircle className="h-5 w-5" />
                  {t.enterNow}
                  <ExternalLink className="h-4 w-4 opacity-80" />
                </a>
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="ios-press h-11 flex-1 border-white/10 bg-white/5 text-zinc-100"
                  onClick={() => void copyAccessUrl()}
                >
                  {copied ? (
                    <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-300" />
                  ) : (
                    <Copy className="mr-2 h-4 w-4" />
                  )}
                  {copied ? "Copied" : t.copyLink}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="ios-press h-11 shrink-0 px-3 text-zinc-500"
                  onClick={() => void signOut()}
                  aria-label={t.signOut}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center gap-2 py-1 text-sm text-emerald-200">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t.linkAppearingSoon}
            </div>
          )
        }
      >
        <MembershipPortalCard tone="success">
          <CardHeader className="space-y-3 pb-3 pt-6 text-center sm:pt-7">
            <MembershipPortalIconBadge tone="emerald" className="h-16 w-16 rounded-full">
              <CheckCircle2 className="h-8 w-8" />
            </MembershipPortalIconBadge>
            <CardTitle className="font-ios-display flex items-center justify-center gap-2 text-xl tracking-tight text-zinc-50 sm:text-2xl">
              <Sparkles className="h-5 w-5 text-amber-300" />
              {t.approvedTitle}
            </CardTitle>
            <CardDescription className="text-sm text-zinc-400">{t.approvedDesc}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-3.5 px-4 pb-5 pt-1 sm:space-y-4 sm:px-6 sm:pb-6">
            {progressSteps}
            {memberDataCard}
            {accessUrl ? (
              <>
                <div className="rounded-2xl border border-[#0088cc]/30 bg-[#0088cc]/10 p-3.5 sm:p-4">
                  <div className="mb-2.5 flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-[#0088cc]" />
                    <span className="font-semibold text-zinc-100">{t.privateLinkLabel}</span>
                  </div>
                  <div className="rounded-xl border border-white/5 bg-black/40 p-2.5">
                    <code className="block break-all font-mono text-[11px] leading-relaxed text-zinc-300 select-all sm:text-xs">
                      {accessUrl}
                    </code>
                  </div>
                </div>
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-200/90">
                  ⚠️ {t.linkPersonalWarning}
                </div>
              </>
            ) : (
              <div className="flex items-start gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-emerald-300" />
                <p>{t.linkAppearingSoon}</p>
              </div>
            )}

            {upgradeVipCard}

            <p className="text-center text-xs text-zinc-500">
              {t.loggedInAs}{" "}
              <code className="rounded bg-black/40 px-1 text-zinc-400">{email}</code>
            </p>

            <div className="flex items-center justify-center border-t border-white/5 pt-2 text-xs">
              <a
                href={`mailto:${BRAND_SUPPORT_EMAIL}`}
                className="inline-flex min-h-11 min-w-[44px] items-center gap-1.5 px-3 text-zinc-500 hover:text-zinc-200"
              >
                <Mail className="h-3.5 w-3.5" />
                {t.contact}
              </a>
            </div>
          </CardContent>
        </MembershipPortalCard>
      </MembershipPortalShell>
    );
  }

  return (
    <MembershipPortalShell
      topSlot={liveStatusPill}
      bottomDock={
        <>
          <Button
            type="button"
            className="ios-press h-12 w-full border-0 bg-white text-base font-semibold text-zinc-950 hover:bg-zinc-100 active:scale-[0.98]"
            onClick={() => void checkApproval(true)}
            disabled={manualChecking || polling}
          >
            {manualChecking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking…
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Check status now
              </>
            )}
          </Button>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Button
              type="button"
              variant="outline"
              className="ios-press h-11 w-full border-amber-500/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
              onClick={() => void notifyAdmin()}
              disabled={notifying || (hasNotified && cooldownRemaining > 0)}
            >
              {notifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Notifying…
                </>
              ) : hasNotified && cooldownRemaining > 0 ? (
                <>
                  <Clock className="mr-2 h-4 w-4" />
                  Wait {Math.ceil(cooldownRemaining / 60000)} min
                </>
              ) : (
                <>
                  <Bell className="mr-2 h-4 w-4" />
                  Notify team
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="ios-press h-11 w-11 shrink-0 px-0 text-zinc-500 hover:bg-white/5 hover:text-zinc-300"
              onClick={() => void signOut()}
              aria-label={t.signOut}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </>
      }
    >
      <MembershipPortalCard tone="warning">
        <CardHeader className="space-y-3 pb-3 pt-6 text-center sm:pt-7">
          <MembershipPortalIconBadge tone="amber">
            <Clock className="h-7 w-7" />
          </MembershipPortalIconBadge>
          <div>
            <CardTitle className="font-ios-display break-words text-[1.35rem] leading-tight tracking-tight text-zinc-50 sm:text-2xl">
              {t.greetingPrefix} {displayName || "there"} 👋
            </CardTitle>
            <CardDescription className="mt-2 text-sm leading-relaxed text-zinc-400">
              {fromPaidVip
                ? t.descPaidVip
                : fromFree
                  ? t.descFree
                  : fromInvitation && postCheckoutMessaging
                    ? t.descAfterPayInvitation
                    : postCheckoutMessaging
                      ? t.descAfterPay
                      : t.descPending}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-3.5 px-4 pb-5 pt-1 sm:space-y-4 sm:px-6 sm:pb-6">
          {progressSteps}
          {memberDataCard}
          {upgradeVipCard}
          {fromFree && profileTelegram && (
            <div className="flex items-center gap-2 rounded-2xl border border-[#0088cc]/20 bg-[#0088cc]/10 p-3">
              <MessageCircle className="h-4 w-4 shrink-0 text-[#0088cc]" />
              <div className="min-w-0">
                <p className="text-[11px] text-zinc-500">{t.telegramAliasLabel}</p>
                <code className="block truncate text-xs text-zinc-300">{profileTelegram}</code>
              </div>
            </div>
          )}
          {fromInvitation && (
            <div className="flex items-center gap-2 rounded-2xl border border-purple-500/30 bg-purple-500/10 p-3">
              <Sparkles className="h-4 w-4 shrink-0 text-purple-300" />
              <p className="text-xs text-purple-200">
                You joined through an exclusive invitation. Your membership gets priority review.
              </p>
            </div>
          )}
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-sm sm:p-4">
            <div className="flex items-start gap-2.5">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              <div className="space-y-1 min-w-0">
                <p className="font-semibold text-amber-100">This can take a few hours</p>
                <p className="text-xs leading-relaxed text-amber-200/80">
                  Every account is approved manually. We&apos;ll email you at{" "}
                  <code className="break-all rounded bg-amber-900/40 px-1 text-amber-200">{email}</code>{" "}
                  once it&apos;s ready — this page also updates by itself.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2 rounded-2xl border border-white/10 bg-white/[0.04] p-3.5 text-sm sm:p-4">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
              <div className="min-w-0">
                <p className="font-semibold text-zinc-100">What happens next?</p>
                <ul className="mt-1.5 space-y-1.5 text-xs leading-relaxed text-zinc-400">
                  <li className="flex gap-2">
                    <span className="text-zinc-600" aria-hidden>
                      1.
                    </span>
                    <span>
                      {fromFree
                        ? "The team reviews my registered details"
                        : isAccessRequest
                          ? "The team reviews my access request"
                          : fromPaidVip
                            ? "The team confirms my VIP payment and activates my access"
                            : "The team verifies my account and payment"}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-zinc-600" aria-hidden>
                      2.
                    </span>
                    <span>My private access link appears here automatically</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-zinc-600" aria-hidden>
                      3.
                    </span>
                    <span>I also get a confirmation email</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <MembershipPortalHint className="pt-1" />

          <p className="border-t border-white/5 pt-3 text-center text-[11px] leading-relaxed text-zinc-600">
            Auto-refreshes every {POLL_INTERVAL_MS / 1000}s ·{" "}
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="inline-flex min-h-10 items-center text-zinc-400 underline underline-offset-2 hover:text-zinc-200"
            >
              <Mail className="mr-0.5 inline h-3 w-3" />
              {t.contact}
            </a>
          </p>
        </CardContent>
      </MembershipPortalCard>
    </MembershipPortalShell>
  );
}
